import { MOCK_DURATION_SECONDS, OVERFLOW_BUFFER_SECONDS } from "./constants";

// ── lesson status helpers ────────────────────────────────────────────────────

function lessonUserFor(lessonsUsersMap, lessonId) {
  return lessonsUsersMap[lessonId];
}

export function isCompleted(lu) {
  return lu?.watched || (lu?.progress || 0) >= 1.0;
}

export function getProgress(lu) {
  if (!lu) return 0.0;
  return lu.watched ? 1.0 : lu.progress || 0.0;
}

export function getStatus(lu) {
  if (isCompleted(lu)) return "completed";
  if (getProgress(lu) > 0) return "in_progress";
  return "not_started";
}

// ── day allocation ──────────────────────────────────────────────────────────

export function itemDuration(queueItem) {
  return queueItem.type === "lesson"
    ? queueItem.lesson.duration || 0
    : MOCK_DURATION_SECONDS;
}

export function allocateItemsForDay(
  queue,
  dailyBudget,
  { usedSeconds = 0, allowOverflow = true } = {}
) {
  const allocated = [];
  let timeUsed = usedSeconds;

  while (queue.length > 0) {
    const nextItem = queue[0];
    const duration = itemDuration(nextItem);

    if (timeUsed + duration <= dailyBudget) {
      const entry = queue.shift();
      allocated.push({ ...entry, overBudget: false });
      timeUsed += duration;
      continue;
    }

    if (!allowOverflow) break;

    if (
      timeUsed + duration <= dailyBudget + OVERFLOW_BUFFER_SECONDS ||
      allocated.length === 0
    ) {
      const entry = queue.shift();
      allocated.push({ ...entry, overBudget: true });
      timeUsed += duration;
    }
    break;
  }

  return allocated;
}

export function itemSortKey(entry, positionMap) {
  if (entry.type === "lesson") {
    return positionMap[entry.lesson.id] ?? Infinity;
  }
  // Mock: place after last lesson of its module
  return (entry._maxLessonPos ?? 0) + 0.5;
}

export function buildDayItems(items, lastModule, positionMap, lessonsUsersMap) {
  const sorted = items
    .slice()
    .sort((a, b) => itemSortKey(a, positionMap) - itemSortKey(b, positionMap));

  const result = [];
  let currentModule = lastModule;

  sorted.forEach((entry) => {
    const modName = entry.moduleName;
    if (modName && modName !== currentModule) {
      currentModule = modName;
      result.push({ type: "module", name: modName });
    }

    if (entry.type === "lesson") {
      const lu = lessonUserFor(lessonsUsersMap, entry.lesson.id);
      result.push({
        type: "lesson",
        id: entry.lesson.id,
        name: entry.lesson.name,
        durationSeconds: entry.lesson.duration || 0,
        progress: getProgress(lu),
        status: getStatus(lu),
        overBudget: !!entry.overBudget,
        courseId: entry.lesson.courseId,
        levelId: entry.lesson.levelId,
      });
    } else if (entry.type === "mock") {
      result.push({ ...entry.mockData, overBudget: !!entry.overBudget });
    }
  });

  return { items: result, lastModule: currentModule };
}

export function buildDay(
  date,
  dayIndex,
  items,
  isToday,
  lastModule,
  dailyBudget,
  positionMap,
  lessonsUsersMap
) {
  const totalSeconds = items.reduce((sum, e) => sum + itemDuration(e), 0);
  const completedSeconds = items.reduce((sum, e) => {
    if (e.type !== "lesson") return sum;
    const lu = lessonUserFor(lessonsUsersMap, e.lesson.id);
    return sum + (e.lesson.duration || 0) * getProgress(lu);
  }, 0);
  const lessonCount = items.filter((e) => e.type === "lesson").length;

  const { items: dayItems, lastModule: newLastModule } = buildDayItems(
    items,
    lastModule,
    positionMap,
    lessonsUsersMap
  );

  const day = {
    date: formatDate(date),
    displayDate: formatDisplayDate(date),
    dayIndex,
    isToday,
    totalPlannedSeconds: totalSeconds,
    plannedLessonsCount: lessonCount,
    dailyBudgetSeconds: dailyBudget,
    dailyProgressPercent:
      totalSeconds > 0
        ? Math.round((completedSeconds / totalSeconds) * 1000) / 10
        : 0,
    lessons: dayItems,
  };

  return { day, lastModule: newLastModule };
}

// ── date helpers ────────────────────────────────────────────────────────────

function formatDate(date) {
  return date.toISOString().split("T")[0];
}

function formatDisplayDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

export function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function isStudyDay(date, weekdayIndexes) {
  return weekdayIndexes.includes(date.getDay());
}
